import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { AccountInfo, EventType, InteractionStatus } from "@azure/msal-browser";
import { BehaviorSubject } from "rxjs";
import { filter, map, switchMap } from "rxjs/operators";
import { environment } from "../../environments/environment";

@Injectable({
    providedIn: "root",
})
export class AuthService {
    // Observable holding current account info
    private currentAccountSubject = new BehaviorSubject<AccountInfo | null>(
        null
    );
    currentAccount$ = this.currentAccountSubject.asObservable();
    private user: AccountInfo = null;

    get userInfo() {
        return this.currentAccountSubject.asObservable();
    }

    get isUserLoggedIn() {
        return this.userInfo.pipe(
            map(
                (user) => user != null && this.validateToken(user.idTokenClaims)
            )
        );
    }

    validateToken(idTokenClaims: any): boolean {
        if (!idTokenClaims || !idTokenClaims.exp) {
            return false;
        }

        const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
        return idTokenClaims.exp > currentTime;
    }

    constructor(
        private msalService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        private router: Router
    ) {
        // On service init, load any cached accounts
        this.loadAccount();

        // Listen for interaction status changes (login, logout, token acquisition)
        this.msalBroadcastService.inProgress$
            .pipe(filter((status) => status === InteractionStatus.None))
            .subscribe(() => {
                this.loadAccount();
            });

        // Only treat an explicit logout as a reason to clear the active account.
        // Background/idle-triggered silent token renewals can momentarily report
        // an empty account list while MSAL's cache is being read/rewritten; if we
        // clear the account on those transient reads, the nav bar flashes hidden
        // even though the user is still logged in.
        this.msalBroadcastService.msalSubject$
            .pipe(
                filter(
                    (msg) =>
                        msg.eventType === EventType.LOGOUT_SUCCESS ||
                        msg.eventType === EventType.LOGOUT_END
                )
            )
            .subscribe(() => {
                this.user = null;
                this.currentAccountSubject.next(null);
            });
    }

    private loadAccount() {
        const accounts = this.msalService.instance.getAllAccounts();

        if (accounts.length > 0) {
            // Choose the first account (customize if needed)
            const activeAccount = accounts[0];
            this.msalService.instance.setActiveAccount(activeAccount);
            this.user = activeAccount;
            this.currentAccountSubject.next(activeAccount);
            console.log("Active account set:", activeAccount);
        } else if (this.user == null) {
            // Nothing was ever logged in; safe to reflect the empty state.
            this.msalService.instance.setActiveAccount(null);
            this.currentAccountSubject.next(null);
            console.log("No active account found");
        } else {
            // Keep the previously known account instead of nulling it out on a
            // transient/empty read (e.g. mid silent token renewal after idle).
            console.log("Transient empty account list ignored, keeping current session");
        }
    }

    // Helper method to get current account synchronously
    getCurrentAccount(): AccountInfo | null {
        return this.currentAccountSubject.value;
    }

    login(returnUrl?: string) {
        const target = returnUrl && returnUrl.startsWith("/") ? returnUrl : "/dashboard";
        this.msalService
            .initialize()
            .pipe(
                switchMap(() =>
                    this.msalService.loginRedirect({
                        scopes: [...environment.protectedResources.api.scopes],
                        redirectStartPage: window.location.origin + target,
                    })
                )
            )
            .subscribe();
    }

    logout() {
        this.msalService.logoutRedirect({
            account: this.getCurrentAccount() ?? undefined,
        });
    }

    isLoggedIn(): boolean {
        return this.getCurrentAccount() != null;
    }

    get isUserAuthorized$() {
        return this.userInfo.pipe(map(this.isUserAuthorizedInternal));
    }

    private isUserAuthorizedInternal(account: AccountInfo) {
        const roles = account?.idTokenClaims?.roles;
        return Array.isArray(roles) && roles.length > 0;
    }

    isUserAuthorized(): boolean {
        return this.isUserAuthorizedInternal(this.user);
    }

}
